import { Map, List } from 'immutable'
import { compose } from 'ramda'
import {
  setIn,
  merge,
  removeIn,
  updateIn,
  filterNot,
  getEq,
  push,
} from 'utils/immutable'
import { COMPLEX, NO_EDGE } from 'utils/constants'
import {
  makeSingleCondition,
  defaultConditionProps,
  makeLogicNode,
} from 'utils/feature'
import * as actions from 'actions/feature/conditions'
import { UPDATE_TEM_SWITCH_CUSTOM_DESCRIPTION } from 'actions/feature/inputs'
import { addLogicNode } from './logicNodes'

const pathToConditions = ['currentFeature', 'conditions']

export default {
  [actions.ADD_SINGLE_CONDITION]: addSingleCondition,
  [actions.DELETE_SINGLE_CONDITION]: deleteSingleCondition,
  [actions.SET_CONDITION_FIELD]: updateConditionField,
  [actions.RESET_CONDITION]: resetCondition,
  [actions.RESET_CONDITION_SHOW_HELP]: resetConditionShowHelp,
  [actions.ADD_SUGGESTED_OUTPUT_NODE]: addOutputNode,
  [actions.MOVE_SINGLE_CONDITION]: moveSingleCondition,
  [actions.MOVE_SINGLE_CONDITION_TO_LOGIC_SET]: moveSingleConditionToLogicSet,
  [UPDATE_TEM_SWITCH_CUSTOM_DESCRIPTION]: updateTemSwitchCustomDescription,
}

function addSingleCondition(
  state,
  { subType, fnId, isSixteenBitCan, isNativeOutput, isOutputBoolean }
) {
  const featureId = state.getIn(['currentFeature', 'feature']).first().get('id')
  const singleCondition = makeSingleCondition(
    subType,
    fnId,
    isSixteenBitCan,
    isNativeOutput,
    isOutputBoolean
  )

  const { uri } = singleCondition

  const setCondition = setIn(
    ['currentFeature', 'conditions', uri],
    singleCondition
  )
  const setOutputQuery = setIn(
    ['currentFeature', 'outputMaps', fnId, 'outputQuery'],
    uri
  )
  const setInFeatureList = updateIn(
    ['currentFeature', 'feature', featureId, 'queries'],
    push(
      Map({
        id: uri,
        schema: 'conditions',
      })
    )
  )

  const transform = compose(setOutputQuery, setInFeatureList, setCondition)

  return transform(state)
}

function deleteSingleCondition(state, { uri, fnId }) {
  const featureId = state.getIn(['currentFeature', 'feature']).first().get('id')

  const removeCondition = removeIn(['currentFeature', 'conditions', uri])
  const resetOutputQuery = removeIn([
    'currentFeature',
    'outputMaps',
    fnId,
    'outputQuery',
  ])
  const removeFromFeatureList = updateIn(
    ['currentFeature', 'feature', featureId, 'queries'],
    filterNot(getEq('id', uri))
  )

  const transform = compose(
    removeFromFeatureList,
    removeCondition,
    resetOutputQuery
  )

  return transform(state)
}

function addOutputNode(state, { output, fnId, tmpId }) {
  const addOutput = setIn(['currentFeature', 'outputs', tmpId], output)

  const appendToFnOutputs = updateIn(
    ['currentFeature', 'outputMaps', fnId, 'outputs'],
    push(tmpId)
  )

  return compose(addOutput, appendToFnOutputs)(state)
}

function updateConditionField(state, { uri, paramName, value }) {
  if (!uri || !paramName) {
    return state
  }
  const pathToField = [...pathToConditions, uri, paramName]
  return setIn(pathToField, value, state)
}

function resetCondition(state, { uri }) {
  return state.updateIn(
    ['currentFeature', 'conditions', uri],
    merge(Map(defaultConditionProps()))
  )
}

function resetConditionShowHelp(state, { uri }) {
  return setIn(['currentFeature', 'conditions', uri, 'showHelp'], false)(state)
}

function moveSingleCondition(state, { condition, fnId }) {
  const { uri, parentQueryUri } = condition

  const featureId = state.getIn(['currentFeature', 'feature']).first().get('id')

  const parentQueryIndex = state
    .getIn(['currentFeature', 'feature', featureId, 'queries'])
    .findIndex((query) => query.get('id') === parentQueryUri)
  const setOutputQuery = setIn(
    ['currentFeature', 'outputMaps', fnId, 'outputQuery'],
    uri
  )

  const removeParentQuery = updateIn(
    ['currentFeature', 'queries'],
    filterNot(getEq('uri', parentQueryUri))
  )

  const removeSiblingQueries = updateIn(
    ['currentFeature', 'conditions'],
    filterNot(getEq('parentQueryUri', parentQueryUri))
  )

  const updateConditionParentQuery = removeIn([
    'currentFeature',
    'conditions',
    uri,
    'parentQueryUri',
  ])

  const updateConditionParentType = removeIn([
    'currentFeature',
    'conditions',
    uri,
    'parentType',
  ])

  const updateFunctionReference = updateIn(
    ['currentFeature', 'conditions', uri],
    (condition) => condition.setIn(['functionUris'], List([fnId]))
  )

  const removeParentQueryReferenceInCurrentFeatureFeature = removeIn([
    'currentFeature',
    'feature',
    featureId,
    'queries',
    parentQueryIndex,
  ])

  const updateCurrentFeatureFeatureQueryReference = updateIn(
    ['currentFeature', 'feature', featureId, 'queries'],
    push(Map({ id: uri, schema: 'conditions' }))
  )

  const transform = compose(
    setOutputQuery,
    removeParentQuery,
    removeSiblingQueries,
    updateConditionParentQuery,
    updateConditionParentType,
    updateFunctionReference,
    removeParentQueryReferenceInCurrentFeatureFeature,
    updateCurrentFeatureFeatureQueryReference
  )

  return transform(state)
}

function moveSingleConditionToLogicSet(state, { condition, fnId, nextIndex }) {
  const logicNode = makeLogicNode(COMPLEX, nextIndex)

  const featureId = state.getIn(['currentFeature', 'feature']).first().get('id')

  const {
    logicNode: { uri },
    conditions: newConditions,
  } = logicNode

  const [{ uri: firstCondition }] = Object.values(newConditions).filter(
    ({ conditionIndex }) => conditionIndex === 0
  )

  const conditionProps = state
    .getIn(['currentFeature', 'conditions', condition.uri])
    .toJS()

  const conditions = Object.entries(newConditions).reduce(
    (results, [id, newCondition]) => {
      const { parentType, parentQueryUri, conditionIndex } = newCondition
      if (id !== firstCondition) return { ...results, [id]: newCondition }
      return {
        ...results,
        [condition.uri]: {
          ...conditionProps,
          conditionIndex,
          parentQueryUri,
          parentType,
          edge: NO_EDGE,
          hysteresis: 0,
        },
      }
    },
    {}
  )

  const logicDetails = {
    logicNode: {
      ...logicNode.logicNode,
      conditions: Object.keys(conditions),
    },
    conditions,
  }

  const stateWithNode = addLogicNode(state, {
    nodeType: COMPLEX,
    nextIndex,
    logicDetails,
  })

  const setOutputQuery = setIn(
    ['currentFeature', 'outputMaps', fnId, 'outputQuery'],
    uri
  )

  const updateFunctionReference = updateIn(
    ['currentFeature', 'queries', uri],
    (query) => query.setIn(['functionUris'], List([fnId]))
  )

  const updateCurrentFeatureFeatureQueryReference = updateIn(
    ['currentFeature', 'feature', featureId, 'queries'],
    (queries) => queries.filter((query) => query.get('id') !== condition.uri)
  )

  const transform = compose(
    setOutputQuery,
    updateFunctionReference,
    updateCurrentFeatureFeatureQueryReference
  )

  return transform(stateWithNode)
}

function updateTemSwitchCustomDescription(
  state,
  { uri, customDescription, conditionUris }
) {
  // If conditionUris array is provided, update all of them (for synchronization)
  if (conditionUris && Array.isArray(conditionUris)) {
    let nextState = state
    conditionUris.forEach((conditionUri) => {
      nextState = setIn(
        [...pathToConditions, conditionUri, 'customDescription'],
        customDescription
      )(nextState)
    })
    return nextState
  }

  // Otherwise, update single URI (original behavior for saga to use)
  return setIn(
    [...pathToConditions, uri, 'customDescription'],
    customDescription
  )(state)
}
